import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStartQuiz } from '../../quiz/useStartQuiz';
import { savePendingQuiz, clearPendingQuiz } from '../../quiz/pendingQuiz';
import EmailVerificationPrompt from '../../components/EmailVerificationPrompt/EmailVerificationPrompt';
import { getOnboardingStatus } from '../../services/authService';
import { getQuizAvailability } from '../../services/quizService';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import { selectChallengeLoader } from '../../services/challengeLoaderService';
import ChallengeLoader from '../../components/ChallengeLoader/ChallengeLoader';
import styles from './VerifyQuiz.module.css';

/**
 * Canonical quiz entry point:
 *   /verify/movie/:tmdbId
 *   /verify/tv/:tmdbId/season/:seasonNumber
 *
 * Every gate is handled here, in order: auth → spoiler notice (newly released
 * movies / completed seasons) → DOB → email verification → quiz start →
 * /quiz/:attemptId. Lifecycle emails, pushes, the Home "Next Up"
 * card, Movie pages and onboarding all route through this page, so there is
 * exactly one code path that creates an attempt on web.
 */
export default function VerifyQuiz() {
    const { kind: routeKind, tmdbId, seasonNumber: seasonParam } = useParams();
    const location = useLocation();
    // The static /verify/movie/… and /verify/tv/… routes outrank /verify/:kind/…,
    // so they carry no :kind param — read it from the path instead.
    const kindParam = routeKind ?? location.pathname.split('/')[2];
    const kind = kindParam === 'tv' ? 'tv' : kindParam === 'movie' ? 'movie' : null;
    const seasonNumber = kind === 'tv' && seasonParam != null ? Number(seasonParam) : null;
    const navigate = useNavigate();
    const { user, loading: authLoading, updateUserData } = useAuth();
    const { start } = useStartQuiz();

    const sourceScreen = location.state?.sourceScreen || new URLSearchParams(location.search).get('source') || 'deep_link';
    const mediaTitle = location.state?.mediaTitle || null;

    const [phase, setPhase] = useState('starting'); // starting | spoiler | blocked | not_ready | failed
    const [spoilerNotice, setSpoilerNotice] = useState(null);
    const [blocked, setBlocked] = useState(null);
    const [outcome, setOutcome] = useState(null);
    const [continuing, setContinuing] = useState(false);
    const [continueError, setContinueError] = useState(null);
    const [continueNotice, setContinueNotice] = useState(null);
    const attemptedRef = useRef(false);
    // Several triggers (button, returning to this tab) can confirm the
    // verification at once; only one resumes the quiz.
    const resumingRef = useRef(false);
    const checkInFlightRef = useRef(false);
    const lastCheckAtRef = useRef(0);
    // Animated loader for the 'starting' phase. Requested alongside the quiz
    // start (never awaited by it); null keeps the standard spinner.
    const [loaderPick, setLoaderPick] = useState({ loader: null, mediaCategories: [] });
    const loaderRequestedRef = useRef(false);

    const validRoute = !!kind && !!tmdbId && (kind === 'movie' || Number.isInteger(seasonNumber));

    const runStart = useCallback(async ({ resumed = false } = {}) => {
        setPhase('starting');
        if (!loaderRequestedRef.current) {
            loaderRequestedRef.current = true;
            selectChallengeLoader(Number(tmdbId), kind).then(setLoaderPick);
        }
        const res = await start({ kind, tmdbId, seasonNumber, sourceScreen, isFirstQuiz: user?.coinCount === 0 ? true : null, resumed });
        if (res.status !== 'blocked' && res.reason !== 'dob_required') clearPendingQuiz();
        if (resumed && (res.status === 'started' || res.status === 'not_ready')) {
            trackEvent(ANALYTICS_EVENTS.QUIZ_RESUMED_AFTER_VERIFICATION, {
                platform: 'web', source_screen: sourceScreen, media_type: kind, media_id: Number(tmdbId),
                ...(seasonNumber != null && { season_number: seasonNumber }),
            });
        }
        if (res.status === 'started') {
            navigate(`/quiz/${res.result.attemptId}`, {
                replace: true,
                state: { quizData: res.result, kind, tmdbId: Number(tmdbId), seasonNumber, mediaTitle },
            });
            return res;
        }
        if (res.status === 'blocked') {
            // The server is the authority: correct a stale cached flag.
            if (user?.emailVerified) updateUserData({ emailVerified: false });
            savePendingQuiz({ kind, tmdbId, seasonNumber, sourceScreen, mediaTitle, userId: user?.id });
            resumingRef.current = false;
            setBlocked({ email: res.email || user?.email || null, pendingCoin: res.pendingCoin ?? null });
            setPhase('blocked');
            return res;
        }
        if (res.status === 'not_ready') { setOutcome(res.result); setPhase('not_ready'); return res; }
        if (res.reason === 'dob_required') {
            savePendingQuiz({ kind, tmdbId, seasonNumber, sourceScreen, mediaTitle, userId: user?.id });
            navigate('/enter-dob', { state: { from: location }, replace: true });
            return res;
        }
        setOutcome(res);
        setPhase('failed');
        return res;
    }, [start, kind, tmdbId, seasonNumber, sourceScreen, mediaTitle, navigate, user, location, updateUserData]);

    useEffect(() => {
        if (authLoading || !user || !validRoute || attemptedRef.current) return;
        attemptedRef.current = true;
        // Newly released titles show a spoiler notice before any attempt is
        // created. The check never blocks the quiz: on failure, start as before.
        (async () => {
            let notice = null;
            try {
                notice = (await getQuizAvailability(Number(tmdbId), kind, seasonNumber))?.spoilerNotice ?? null;
            } catch {
                notice = null;
            }
            if (notice) {
                setSpoilerNotice(notice);
                setPhase('spoiler');
                return;
            }
            runStart();
        })();
    }, [authLoading, user, validRoute, runStart, tmdbId, kind, seasonNumber]);

    const backTarget = `/movie/${kind}/${tmdbId}${kind === 'tv' && seasonNumber != null ? `/season/${seasonNumber}` : ''}`;
    const verifyContext = { source_screen: sourceScreen, media_type: kind, media_id: Number(tmdbId) };

    // Ask the server (never a client flag or link parameter) whether the
    // email is verified; resume the exact quiz only when it says so.
    // `trigger`: 'button' | 'return' (came back to this tab) | 'resend'.
    const checkVerification = useCallback(async (trigger) => {
        if (checkInFlightRef.current || resumingRef.current) return;
        checkInFlightRef.current = true;
        lastCheckAtRef.current = Date.now();
        if (trigger === 'button') { setContinuing(true); setContinueError(null); setContinueNotice(null); }
        try {
            const status = await getOnboardingStatus();
            const verified = !!status?.emailVerified;
            trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_CHECKED, { platform: 'web', ...verifyContext, trigger, result: verified ? 'verified' : 'not_verified' });
            if (!verified) {
                if (trigger === 'button') {
                    setContinueNotice(`We haven't been able to confirm your verification yet. Open the link in the email we sent to ${blocked?.email || 'your inbox'}, then try again.`);
                }
                return;
            }
            updateUserData({ emailVerified: true });
            if (resumingRef.current) return;
            resumingRef.current = true;
            await runStart({ resumed: true });
        } catch (err) {
            if (trigger === 'button') {
                trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_CHECKED, { platform: 'web', ...verifyContext, trigger, result: 'error' });
                setContinueError(err?.isNetworkError ? "You're offline. Check your connection and try again." : "We couldn't check right now. Please try again in a moment.");
            }
        } finally {
            checkInFlightRef.current = false;
            setContinuing(false);
        }
    }, [blocked?.email, runStart, updateUserData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Returning to this tab (e.g. from the inbox or the tab the link opened):
    // one check per return, at most every 10s. No polling.
    useEffect(() => {
        if (phase !== 'blocked') return undefined;
        const onReturn = () => {
            if (document.visibilityState === 'hidden') return;
            if (Date.now() - lastCheckAtRef.current < 10_000) return;
            checkVerification('return');
        };
        document.addEventListener('visibilitychange', onReturn);
        window.addEventListener('focus', onReturn);
        return () => {
            document.removeEventListener('visibilitychange', onReturn);
            window.removeEventListener('focus', onReturn);
        };
    }, [phase, checkVerification]);

    const handleNotNow = () => {
        clearPendingQuiz();
        trackEvent(ANALYTICS_EVENTS.EMAIL_VERIFICATION_DISMISSED, { platform: 'web', ...verifyContext });
        navigate(backTarget);
    };

    if (!validRoute) {
        return (
            <div className={styles.page}>
                <div className={styles.card}>
                    <h1 className={styles.title}>That link isn't a challenge we recognise</h1>
                    <Link className={styles.primaryBtn} to="/discover">Choose a Movie</Link>
                </div>
            </div>
        );
    }

    if (authLoading) return <div className={styles.page}><div className={styles.spinner} /></div>;

    if (!user) {
        // Remember the quiz, then send them to log in / sign up and come back here.
        savePendingQuiz({ kind, tmdbId, seasonNumber, sourceScreen, mediaTitle });
        return <Navigate to="/login" state={{ from: location }} replace />;
    }



    return (
        <div className={styles.page}>
            {phase === 'starting' && (
                <div className={styles.card}>
                    <ChallengeLoader
                        loader={loaderPick.loader}
                        mediaCategories={loaderPick.mediaCategories}
                        fallback={<div className={styles.spinner} data-testid="verify-quiz-spinner" />}
                    />
                    <h1 className={styles.title}>Preparing your challenge…</h1>
                    <p className={styles.body}>8 questions · pass with 5{mediaTitle ? ` · ${mediaTitle}` : ''}</p>
                </div>
            )}

            {phase === 'spoiler' && (
                <div className={styles.card} role="dialog" aria-labelledby="spoiler-title">
                    <span className={styles.icon} aria-hidden="true">⚠️</span>
                    <h1 id="spoiler-title" className={styles.title}>Spoiler warning</h1>
                    <p className={styles.body}>{spoilerNotice}{mediaTitle ? ` · ${mediaTitle}` : ''}</p>
                    <div className={styles.actions}>
                        <button className={styles.primaryBtn} onClick={() => runStart()}>Start challenge</button>
                        <Link className={styles.secondaryBtn} to={backTarget}>Back to title</Link>
                    </div>
                </div>
            )}

                        {phase === 'blocked' && (
                <EmailVerificationPrompt
                    email={blocked?.email}
                    pendingCoin={blocked?.pendingCoin}
                    onContinue={() => checkVerification('button')}
                    onAlreadyVerified={() => checkVerification('resend')}
                    onNotNow={handleNotNow}
                    onChangeEmail={() => navigate('/settings/account')}
                    continuing={continuing}
                    error={continueError}
                    notice={continueNotice}
                    context={verifyContext}
                />
            )}

            {phase === 'not_ready' && (
                <div className={styles.card}>
                    <span className={styles.icon} aria-hidden="true">⏳</span>
                    <h1 className={styles.title}>
                        {outcome?.reason === 'legendary_achieved' ? 'You already hold Legendary here' : 'Not quite yet'}
                    </h1>
                    <p className={styles.body}>
                        {outcome?.message || 'This challenge is not available right now.'}
                        {outcome?.availableAt && outcome.reason !== 'legendary_achieved' && (
                            <> Available {new Date(outcome.availableAt).toLocaleString()}.</>
                        )}
                    </p>
                    <Link className={styles.primaryBtn} to={backTarget}>Back to title</Link>
                </div>
            )}

            {phase === 'failed' && (
                <div className={styles.card}>
                    <span className={styles.icon} aria-hidden="true">😬</span>
                    <h1 className={styles.title}>
                        {outcome?.reason === 'generation_timeout' ? 'This one is taking a while' : "We couldn't prepare your challenge"}
                    </h1>
                    <p className={styles.body}>{outcome?.message}</p>
                    <div className={styles.actions}>
                        {['generation_timeout', 'generation_failed', 'network', 'unknown'].includes(outcome?.reason) && (
                            <button className={styles.primaryBtn} onClick={() => { attemptedRef.current = true; runStart(); }}>Try again</button>
                        )}
                        <Link className={styles.secondaryBtn} to="/discover">Choose another title</Link>
                    </div>
                </div>
            )}
        </div>
    );
}
